// iTrace 监控 SDK 初始化（需在应用渲染前导入，以尽早捕获异常和请求）
import React from 'react';
import { HashRouter } from 'react-router-dom';
import { createRoot } from 'react-dom/client';

import App from '@/app/App';
import { configureApiGateway } from '@/app/config/apiConfig';
import { installFeatureFlagTraps } from '@/hooks/useFeatureFlag';
import { setItraceReady } from '@/services/monitoring';

import './styles/index.css';

// 设置 iTrace 为就绪状态，开始上报日志
setItraceReady();

// ============== 运行时配置初始化 ==============
// 构建期兜底值
window.__BACKEND_GATEWAY__ = typeof __BACKEND_GATEWAY__ !== 'undefined' ? __BACKEND_GATEWAY__ : '';
window.__APP_RELEASE__ = typeof __APP_RELEASE__ !== 'undefined' ? __APP_RELEASE__ : '';

const isDev = typeof __IS_DEV__ !== 'undefined' ? __IS_DEV__ : false;
const isLocalEnvironment = typeof __ENV_TYPE__ !== 'undefined' && __ENV_TYPE__ === 'local';

type CanvasFeatureFlagPayload = {
  canvas_access_enabled?: unknown;
};

function applyCanvasFeatureFlags(payload: CanvasFeatureFlagPayload) {
  window.__CANVAS_ACCESS_ENABLED__ = payload.canvas_access_enabled === true;
}

function bootstrap() {
  const container = document.getElementById('root');
  if (!container) {
    throw new Error('Cannot find root element');
  }
  createRoot(container).render(
    <React.StrictMode>
      <HashRouter>
        <App />
      </HashRouter>
    </React.StrictMode>,
  );
}

if (isDev) {
  // 本地环境直接放行；连接日常、预发、线上时读取对应后端的真实配置。
  window.__CANVAS_ACCESS_ENABLED__ = isLocalEnvironment;
  window.__FEATURE_ADVANCED__ = false;
  window.__FEATURE_DEV_TOOLS__ = true;
  // 安装 defineProperty trap：后续控制台直接赋值即可触发组件重渲染
  installFeatureFlagTraps();
  bootstrap();

  if (!isLocalEnvironment) {
    fetch('/api/public/feature-flags/canvas')
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Canvas feature flags request failed: ${res.status}`);
        }
        return res.json();
      })
      .then(applyCanvasFeatureFlags)
      .catch(() => applyCanvasFeatureFlags({}));
  }
} else {
  // 生产模式：先立即渲染，feature flags 异步更新
  // installFeatureFlagTraps 已安装 defineProperty setter，
  // 后续赋值会自动触发 useSyncExternalStore 订阅的组件重渲染
  installFeatureFlagTraps();
  bootstrap();

  fetch('/bff/feature-flags')
    .then((res) => res.json())
    .then((data) => {
      Object.entries(data).forEach(([key, value]) => {
        (window as any)[`__${key}__`] = value;
      });
      if (typeof data.BACKEND_GATEWAY === 'string') {
        configureApiGateway(data.BACKEND_GATEWAY);
      }
    })
    .catch(() => {
      // 接口失败：feature flag 全 false，BACKEND_GATEWAY 保留构建期兜底
      window.__CANVAS_ACCESS_ENABLED__ = false;
      window.__FEATURE_ADVANCED__ = false;
      window.__FEATURE_DEV_TOOLS__ = false;
    });
}
